'use strict';

import React, {Component} from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {Link} from "react-router-dom";

import {FaArrowCircleRight, FaPlus, FaLink} from "react-icons/fa";

import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";

import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import {pluralize, size} from "@uw-it-sis/lib-react/lib/Utils";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import HelpPopover from "@uw-it-sis/lib-react/lib/HelpPopover";

import PlanItemList from "../common/PlanItemList";
import planItemModalStore from "../registration/PlanItemModalStore";
import IntendedGraduation from "../common/IntendedGraduation";
import ExtraordinaryCircumstances from "../common/ExtraordinaryCircumstances";
import {PlanItemType} from "../lib/Constants";

class PlanTerm extends Component {
    constructor(props) {
        super(props);
        this.props = props;

        this[this.props.termShortName] = React.createRef();
        this.handleAddToPlanButtonClick = this.handleAddToPlanButtonClick.bind(this);
    }

    componentDidMount() {
        if (this.props.isAnchored) {
            this[this.props.termShortName].current.scrollIntoView();
        }
    }

    handleAddToPlanButtonClick() {
        let analyticsData = {
            context: "PlanView-addToPlan"
        };
        planItemModalStore.openModal('add', this.props.term, analyticsData);
    }

    /**
     * Check to see if a term id exists in the list of published terms
     */
    hasPublishedTerm(termId) {
        let state = false;
        for (let term of termStore.get('publishedTerms')) {
            if (term.id === termId) state = true;
        }
        return state;
    }

    render() {
        let term = this.props.term;

        let termColor = "beige";
        let termPeriod = "future";
        if ( term.completedTerm ) {
            termColor = "light-gray";
            termPeriod = "past";
        } else if ( term.currentTermForView ) {
            termColor = "purple";
            termPeriod = "current";
        }

        let isPublished = this.hasPublishedTerm(term.atpId);

        // Build a term heading and create a link to reg view if term is published
        let termHeader;
        let termTitle = <h2 className={classnames("h4 mb-0 d-inline-block", {"text-white": term.currentTermForView}, {"text-dark": term.completedTerm || ! isPublished})}>{term.qtrYear}</h2>;
        if ( isPublished ) {
            // Convert term id to a lowercase, no spaces, shortened descriptor (e.g. 'sp18')
            let termShortName = TermUtils.makeTermLinkIdentifierFromTermId(term.atpId);
            termHeader =
                <Link to={`/${termShortName}`} className={classnames("d-inline-flex align-items-center text-decoration-none")}>
                    {termTitle}
                    <FaArrowCircleRight className={classnames("ms-3", {"text-white": term.currentTermForView})} size="1.25rem" aria-hidden={true} />
                </Link>;
        } else {
            termHeader = termTitle;
        }

        // Default "0" plan items display, differs for past vs current/future
        let zeroItemComponent;
        let zeroItemsClasses = classnames("mt-3", "px-3", {"border border-light-gray p-3 mb-4": term.recommendedList.length > 0});
        if (term.openForPlanning) {
            zeroItemComponent =
                <div className={zeroItemsClasses}>
                    <p className="lead"><strong>No planned items yet</strong></p>
                    <p><Button variant="inline-link" onClick={this.handleAddToPlanButtonClick}>Add a course or a placeholder</Button> and start planning.</p>
                    <p className="mb-0">Want to explore courses? <a href="/course">Find courses</a> of interest to you.</p>
                </div>;
        } else {
            zeroItemComponent =
                <div className={zeroItemsClasses}>
                    <p className="lead"><strong>No courses were planned or registered for this quarter.</strong></p>
                </div>;
        }

        if ( term.completedTerm ) {
            zeroItemComponent =
                <div className={zeroItemsClasses}>
                    <p className="lead"><strong>No completed courses found</strong></p>
                    <p>No academic history found for this quarter.</p>
                </div>;
        }

        let planItemGroups = [];

        // Determine if term is opening for planning for credit display and add to plan action
        let isOpenForPlanning = ! term.completedTerm && term.openForPlanning;
        // Plan item buckets in display order
        let groups = Object.keys(PlanItemType).filter(type => type !== PlanItemType.recommended);
        // Determine the number of items total across all buckets
        let numPlanItems = groups.map(type => term[`${type}List`].length ).reduce((a, b) => a + b);

        let foundFirstGroupWithItems = false;

        if ( numPlanItems > 0 ) {

            let termCredit;
            // Length of registered and planned items together
            let numRegAndPlanned = groups.slice(0, 2).map(type => term[`${type}List`].length ).reduce((a, b) => a + b);
            // Length of registered items
            let numReg = term.registeredList.length;
            // Figure out which length to compare, if past term, use just registered, if current or future, registered and planned
            let lengthCheck = isOpenForPlanning ? numRegAndPlanned : numReg;
            if ( lengthCheck > 0 ) {
                termCredit =
                    <div key="credit" className="d-flex justify-content-between fw-bold mb-2 mx-2">
                        <div className="d-inline-flex align-items-center">
                            {pluralize("Credit", term.credits)}
                            <HelpPopover title={pluralize("Credit", term.credits)} placement="top" className="ms-2 text-muted">
                                {
                                    term.completedTerm ?
                                        "The number of credits registered for the past quarter."
                                        :
                                        "The number of credits you have planned and/or enrolled in this quarter."
                                }
                            </HelpPopover>
                        </div>
                        <div>
                            {term.credits}
                        </div>
                    </div>;
            }

            planItemGroups = groups.map( (type, i) => {
                let showBorder = true;
                if (!foundFirstGroupWithItems) {
                    // flip this to true once the first non-empty set of items is found
                    foundFirstGroupWithItems = size(term[`${type}List`]) > 0;

                    // Don't show the border along the top for the first group discovered with items
                    showBorder = false;
                }

                let creditComponentForList;

                // Send the term credit component to be included if the term is open and this is the planned set of items,
                // or if the term is not open and this is the registered set of items
                if ( (isOpenForPlanning && type === PlanItemType.planned) ||
                    (!isOpenForPlanning && type === PlanItemType.registered) ) {
                    creditComponentForList = termCredit;
                }

                return (
                    <PlanItemList termColor={termColor}
                                  type={type}
                                  creditComponent={creditComponentForList}
                                  items={term[`${type}List`]}
                                  termPeriod={termPeriod}
                                  openForPlanning={term.openForPlanning}
                                  isPublished={isPublished}
                                  showBorder={showBorder}
                                  key={i} />
                );
            });
        }

        if (term.recommendedList?.length > 0) {
            // if there was no prior group with items, don't show any borders, otherwise, show them
            let showBorder = foundFirstGroupWithItems;

            planItemGroups.push(
                <PlanItemList termColor={termColor}
                              type={PlanItemType.recommended}
                              items={term.recommendedList}
                              termPeriod={termPeriod}
                              openForPlanning={term.openForPlanning}
                              isPublished={isPublished}
                              showBorder={showBorder}
                              key={planItemGroups.length} />
            );
        }

        let bodyComponent = zeroItemComponent;

        if (planItemGroups.length > 0) {
            bodyComponent = planItemGroups;
        }

        // Build "add to plan" button component for adding if term is current/future
        let button = null;
        let isAdviser = appStore.isAdviser();

        if (isOpenForPlanning) {
            button =
                <Card.Footer className="bg-transparent pb-3 pt-0 d-grid">
                    <Button variant="primary"
                            className="d-flex align-items-center justify-content-center"
                            size="sm"
                            onClick={this.handleAddToPlanButtonClick}
                            aria-haspopup={true}>
                        <FaPlus className="me-2 fs-7" aria-hidden={true} />
                        {(! isAdviser) ? "Add to Plan":"Recommend"}
                    </Button>
                </Card.Footer>;
        }

        let clusterLegend = <small className="mt-2 lh-1 d-block text-muted px-2"><FaLink className="me-1 text-muted fs-7" aria-hidden={true} style={{transform: "scaleX(-1)"}} /> Cluster registration section</small>;

        return (
            <>
                <Card ref={this[this.props.termShortName]} border={termColor} className="border-4 w-100 h-100">
                    <Card.Header className={`bg-${termColor} rounded-0 pt-1 px-2 pb-2`}>
                        <div className="d-flex align-items-center justify-content-between">
                            {termHeader}
                            <IntendedGraduation termId={term.atpId} className={`text-${term.currentTermForView ? 'white':'purple'}`} />
                        </div>
                        <ExtraordinaryCircumstances termId={term.atpId} className={term.currentTermForView ? 'text-white':null} />
                    </Card.Header>
                    <Card.Body className="pt-0 px-0 pb-3">
                        {bodyComponent}
                    </Card.Body>
                    {button}
                </Card>
                {term.showClusterLegend ? clusterLegend : ""}
            </>
        );
    }
}

PlanTerm.propTypes = {
    term: PropTypes.object.isRequired,
    isAnchored: PropTypes.bool,
    termShortName: PropTypes.string
};

PlanTerm.defaultProps = {
    term: {},
    isAnchored: false,
    termShortName: ""
};

export default PlanTerm;